// Drive the geotrack simulator on a timer and expose its output in the same
// shape as the live Firestore data.
import { useEffect, useMemo, useRef, useState } from 'react';

import { compact } from 'lodash';

import {
  SIMULATION_SPEEDUP,
  SimulatedTruckPlan,
  createStandInOperators,
  createTruckPlan,
  getSimulatedHistory,
  getSimulatedPoint,
} from '@/components/LiveMap/geotrackSimulator';
import {
  GeotrackPoint,
  LiveMapJob,
  LiveMapOperator,
  TruckLocation,
  buildTruckLocation,
  getPrimaryJobId,
} from '@/components/LiveMap/liveMapModel';

import { liveMapSimulatorEnabled } from '@/env';

const TICK_MS = 1000;
const BACKFILL_MS = 6 * 60 * 1000; // ~1 simulated hour at SIMULATION_SPEEDUP=10
const BACKFILL_STEP_MS = 5000;
const MAX_TRAIL_POINTS = 600;
const RECENT_POINTS = 20;

type SimulationState = {
  locations: Record<string, TruckLocation>;
  trails: Record<string, GeotrackPoint[]>;
};

/**
 * Whether the simulator can be offered in this environment. Enabled with
 * NEXT_PUBLIC_LIVE_MAP_SIMULATOR=true (e.g. in `.env.local`).
 * @returns {boolean}
 */
export function getIsSimulatorAvailable(): boolean {
  return liveMapSimulatorEnabled;
}

/**
 * Simulate GPS fixes for the operators on today's jobs. Jobs nobody is assigned
 * to get stand-in trucks so an empty day can still be demoed.
 * @param {LiveMapJob[]} jobs
 * @param {LiveMapOperator[]} operators - Real operators from JobAssignments.
 * @param {boolean} enabled
 * @returns {{ standInOperators: LiveMapOperator[], locations: Record<string, TruckLocation>, trails: Record<string, GeotrackPoint[]> }}
 */
export function useSimulatedTrucks(
  jobs: LiveMapJob[],
  operators: LiveMapOperator[],
  enabled: boolean
): {
  standInOperators: LiveMapOperator[];
  locations: Record<string, TruckLocation>;
  trails: Record<string, GeotrackPoint[]>;
} {
  const startMsRef = useRef(Date.now());
  const [state, setState] = useState<SimulationState>({
    locations: {},
    trails: {},
  });

  const activeOperators = useMemo(
    () => operators.filter((operator) => !!getPrimaryJobId(operator)),
    [operators]
  );
  const standInOperators = useMemo(
    () => (enabled ? createStandInOperators(jobs, activeOperators) : []),
    [enabled, jobs, activeOperators]
  );

  const plans = useMemo<SimulatedTruckPlan[]>(() => {
    if (!enabled) return [];
    const jobsById = Object.fromEntries(jobs.map((job) => [job.id, job]));
    return compact(
      [...activeOperators, ...standInOperators].map((operator) => {
        const job = jobsById[getPrimaryJobId(operator) || ''];
        return job ? createTruckPlan(operator.uid, job) : null;
      })
    );
  }, [enabled, jobs, activeOperators, standInOperators]);

  const plansKey = plans.map((plan) => `${plan.uid}:${plan.jobId}`).join(',');

  useEffect(() => {
    if (!enabled) {
      setState({ locations: {}, trails: {} });
      return;
    }
    const startMs = startMsRef.current;
    const nowMs = Date.now();
    const trails: Record<string, GeotrackPoint[]> = Object.fromEntries(
      plans.map((plan) => [
        plan.uid,
        getSimulatedHistory(
          plan,
          startMs,
          nowMs,
          BACKFILL_MS,
          BACKFILL_STEP_MS
        ),
      ])
    );

    const publish = () => {
      const locations: Record<string, TruckLocation> = {};
      Object.entries(trails).forEach(([uid, trail]) => {
        const location = buildTruckLocation(uid, trail.slice(-RECENT_POINTS));
        if (location) locations[uid] = location;
      });
      setState({ locations, trails: { ...trails } });
    };
    publish();

    const interval = setInterval(() => {
      const tickMs = Date.now();
      plans.forEach((plan) => {
        const point = getSimulatedPoint(
          plan,
          (tickMs - startMs) * SIMULATION_SPEEDUP,
          tickMs
        );
        trails[plan.uid] = [...(trails[plan.uid] || []), point].slice(
          -MAX_TRAIL_POINTS
        );
      });
      publish();
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [enabled, plansKey]);

  return { standInOperators, ...state };
}
